// Conversion between the /workflows API (a trigger config plus a flat list of
// steps naming their parent) and the editor's nodes and edges.

import type {
  SignalBriefVerdict,
  Workflow,
  WorkflowCreate,
  WorkflowStepInput,
  WorkflowTriggerConfig,
  WorkflowUpdate,
} from "../../../api/client.ts";
import { HIVE_DATA_SOURCE, SIGNAL_TRIGGER, signalTriggerConfigOf } from "../../../api/client.ts";
import { NODE_HEIGHT, NODE_WIDTH, Y_GAP, layoutTree } from "./layout.ts";
import {
  NO_ORDER_DEFAULTS,
  TRIGGER_ID,
  isActionKind,
  isTriggerType,
  type ActionData,
  type AgentData,
  type AgentInfo,
  type ConditionData,
  type ConditionEdge,
  type OrderDefaults,
  type StepData,
  type StepKind,
  type StepNode,
  type TriggerData,
  type TriggerType,
} from "./types.ts";

export interface DisplayLookups {
  dataTypeLabel: (slug: string) => string | undefined;
  listName: (id: string) => string | undefined;
  // What an agent step shows about the agent it runs.
  agentInfo: (id: string) => AgentInfo;
}

export interface Graph {
  nodes: StepNode[];
  edges: ConditionEdge[];
}

// What the editor saves: everything the API stores, nothing display-only.
export interface WorkflowPayload {
  name: string;
  active: boolean;
  trigger_type: TriggerType;
  trigger_config: WorkflowTriggerConfig;
  steps: WorkflowStepInput[];
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const numOrNull = (v: unknown): number | null => (typeof v === "number" ? v : null);
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

// A "Create prospect order" step's defaults: the fields the editor exposes;
// any other default the API holds stays in `base_config`.
function orderDefaultsOf(raw: unknown): OrderDefaults {
  if (!isRecord(raw)) return { ...NO_ORDER_DEFAULTS };
  return { requested_count: numOrNull(raw.requested_count) };
}

// The API stores a money cap as a 2-dp string ("25.00") so it round-trips
// exactly; the editor works in numbers and sends numbers.
const numericString = /^-?\d+(\.\d+)?$/;
const fromStored = (v: unknown): unknown => (typeof v === "string" && numericString.test(v) ? Number(v) : v);

// An agent step's opted-in data sources: an object of objects, `hive` dropped
// (it is implicit and never stored), anything else ignored.
function dataSourcesOf(raw: unknown): AgentData["data_sources"] {
  if (!isRecord(raw)) return {};
  const out: AgentData["data_sources"] = {};
  for (const [key, config] of Object.entries(raw)) {
    if (key === HIVE_DATA_SOURCE || !isRecord(config)) continue;
    out[key] = Object.fromEntries(Object.entries(config).map(([k, v]) => [k, fromStored(v)]));
  }
  return out;
}

export function sized(n: StepNode): StepNode {
  return { ...n, width: NODE_WIDTH, height: NODE_HEIGHT };
}

export function edgeId(source: string, target: string): string {
  return `${source}->${target}`;
}

export function makeEdge(source: string, target: string, data: ConditionData): ConditionEdge {
  return {
    id: edgeId(source, target),
    source,
    sourceHandle: "out",
    target,
    targetHandle: "in",
    type: "condition",
    data,
  };
}

export function triggerNode(data: TriggerData, position = { x: 0, y: 0 }): StepNode {
  return sized({ id: TRIGGER_ID, type: "step", position, data, deletable: false });
}

function triggerDataFor(
  workflow: Pick<Workflow, "trigger_type" | "trigger_config">,
  lookups: DisplayLookups,
): TriggerData {
  if (workflow.trigger_type === SIGNAL_TRIGGER) {
    const trigger = signalTriggerConfigOf(workflow);
    return {
      kind: "trigger",
      trigger_type: SIGNAL_TRIGGER,
      data_type: trigger.data_type,
      data_type_label: lookups.dataTypeLabel(trigger.data_type) ?? trigger.data_type,
      prompt: trigger.prompt,
      match_mode: trigger.match_mode ?? "llm",
      source: trigger.source ?? {},
    };
  }
  return { kind: "trigger", trigger_type: "prospect_order" };
}

/**
 * The graph of a workflow being created: just its trigger. `source` is the
 * data type's settings to start from (its config fields' defaults).
 */
export function emptyGraph(
  triggerType: TriggerType,
  dataType: string,
  lookups: DisplayLookups,
  source: Record<string, unknown> = {},
): Graph {
  const trigger_config =
    triggerType === SIGNAL_TRIGGER ? { data_type: dataType, prompt: "", source } : {};
  return {
    nodes: [triggerNode(triggerDataFor({ trigger_type: triggerType, trigger_config }, lookups))],
    edges: [],
  };
}

function actionData(
  kind: Exclude<StepKind, "trigger">,
  config: Record<string, unknown>,
  enabled: boolean,
  lookups: DisplayLookups,
): ActionData {
  const base_config = { ...config };
  switch (kind) {
    case "agent": {
      const agentId = str(config.agent_id);
      return {
        kind,
        agent_id: agentId,
        ...lookups.agentInfo(agentId),
        instructions: str(config.instructions),
        data_sources: dataSourcesOf(config.data_sources),
        model: str(config.model).trim(),
        max_runs_per_day: numOrNull(config.max_runs_per_day),
        enabled,
        base_config,
      };
    }
    case "add_to_prospect_list": {
      const listId = str(config.collection_id);
      return {
        kind,
        collection_id: listId,
        collection_name: (listId && lookups.listName(listId)) || "",
        enabled,
        base_config,
      };
    }
    case "slack_message":
      return {
        kind,
        channel_id: str(config.channel_id),
        channel_name: str(config.channel_name),
        enabled,
        base_config,
      };
    case "create_prospect_order":
      return {
        kind,
        instructions: str(config.instructions),
        defaults: orderDefaultsOf(config.defaults),
        assignee_user_ids: strings(config.assignee_user_ids),
        on_repeat: config.on_repeat === "new" ? "new" : "update",
        max_orders_per_day: numOrNull(config.max_orders_per_day),
        enabled,
        base_config,
      };
  }
}

// What a condition under an agent says, read whatever its contract: the agent
// definitions (which say the contract) may not have loaded yet.
function agentConditionData(condition: Record<string, unknown>): ConditionData {
  const data: ConditionData = {};
  if (Array.isArray(condition.verdicts)) {
    data.verdicts = condition.verdicts as SignalBriefVerdict[];
    data.min_confidence = numOrNull(condition.min_confidence);
  }
  if ("min_results" in condition) data.min_results = numOrNull(condition.min_results);
  if (typeof condition.field === "string") {
    data.field = condition.field;
    data.op = condition.op as ConditionData["op"];
    data.value = condition.value;
  }
  return data;
}

function conditionData(parent: StepData, condition: Record<string, unknown>): ConditionData {
  switch (parent.kind) {
    case "trigger":
      return parent.trigger_type === SIGNAL_TRIGGER
        ? { min_rating: numOrNull(condition.min_rating) }
        : {};
    case "agent":
      return agentConditionData(condition);
    default:
      return {};
  }
}

/**
 * Nodes and edges for a saved workflow. Steps of a type the editor doesn't
 * know are skipped (the API only has the ones it does). Stored positions are
 * used when every step has one; otherwise the tree is laid out afresh.
 */
export function toGraph(
  workflow: Workflow,
  lookups: DisplayLookups,
  opts: { triggerPosition?: { x: number; y: number } } = {},
): Graph {
  const triggerData = triggerDataFor(workflow, lookups);
  const steps = workflow.steps.filter((s) => isActionKind(s.step_type));
  const dataById = new Map<string, StepData>([[TRIGGER_ID, triggerData]]);

  const nodes: StepNode[] = [triggerNode(triggerData)];
  const edges: ConditionEdge[] = [];

  for (const s of steps) {
    const data = actionData(s.step_type as Exclude<StepKind, "trigger">, s.config ?? {}, s.enabled, lookups);
    dataById.set(s.id, data);
    nodes.push(
      sized({
        id: s.id,
        type: "step",
        position: s.position ? { x: s.position.x, y: s.position.y } : { x: 0, y: 0 },
        data,
        deletable: true,
      }),
    );
  }
  for (const s of steps) {
    const source = s.parent_step_id ?? TRIGGER_ID;
    const parent = dataById.get(source);
    // A parent the editor can't show leaves the step unconnected.
    if (parent) {
      edges.push(
        makeEdge(source, s.id, {
          ...conditionData(parent, s.condition ?? {}),
          requires_approval: !!s.requires_approval,
        }),
      );
    }
  }

  if (steps.some((s) => !s.position)) {
    return { nodes: layoutTree(nodes, edges), edges };
  }

  // The trigger's position isn't stored: keep where it was, or centre it
  // above the root steps.
  let position = opts.triggerPosition;
  if (!position) {
    const roots = steps.filter((s) => s.parent_step_id === null && s.position);
    if (roots.length) {
      const xs = roots.map((s) => s.position!.x);
      const minY = Math.min(...roots.map((s) => s.position!.y));
      position = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: minY - NODE_HEIGHT - Y_GAP };
    } else if (steps.length) {
      const minY = Math.min(...steps.map((s) => s.position!.y));
      position = { x: steps[0].position!.x, y: minY - NODE_HEIGHT - Y_GAP };
    }
  }
  if (position) nodes[0] = { ...nodes[0], position };
  return { nodes, edges };
}

function configFor(data: ActionData): Record<string, unknown> {
  switch (data.kind) {
    case "agent": {
      const config: Record<string, unknown> = {
        ...data.base_config,
        agent_id: data.agent_id,
        instructions: data.instructions,
      };
      if (data.max_runs_per_day != null) config.max_runs_per_day = data.max_runs_per_day;
      else delete config.max_runs_per_day;
      if (data.model) config.model = data.model;
      else delete config.model;
      // The base_config spread would resurrect a source the user removed.
      if (Object.keys(data.data_sources).length) config.data_sources = data.data_sources;
      else delete config.data_sources;
      return config;
    }
    case "add_to_prospect_list":
      return { ...data.base_config, collection_id: data.collection_id };
    case "slack_message":
      return { ...data.base_config, channel_id: data.channel_id, channel_name: data.channel_name };
    case "create_prospect_order": {
      // Blank defaults are left out; ones the editor doesn't show are kept.
      const stored = isRecord(data.base_config.defaults) ? data.base_config.defaults : {};
      const defaults: Record<string, unknown> = { ...stored };
      for (const [key, value] of Object.entries(data.defaults)) {
        if (value === null) delete defaults[key];
        else defaults[key] = value;
      }
      const config: Record<string, unknown> = {
        ...data.base_config,
        instructions: data.instructions,
        on_repeat: data.on_repeat,
        defaults,
        assignee_user_ids: data.assignee_user_ids,
      };
      if (data.max_orders_per_day != null) config.max_orders_per_day = data.max_orders_per_day;
      else delete config.max_orders_per_day;
      return config;
    }
  }
}

function agentConditionFor(parent: AgentData, data: ConditionData | undefined): Record<string, unknown> {
  const verdicts = () => ({
    verdicts: data?.verdicts ?? [],
    min_confidence: data?.min_confidence ?? null,
  });
  const minResults = () => (data?.min_results != null ? { min_results: data.min_results } : {});
  const field = () =>
    data?.field ? { field: data.field, op: data.op ?? "eq", value: data.value ?? null } : {};
  switch (parent.contract) {
    case "signal_brief":
      return verdicts();
    case "prospect_list":
    case "contact_list":
      return minResults();
    case "structured":
      return field();
    case "text":
      return {};
    default:
      // The agent isn't known yet: keep whatever the condition said.
      return { ...(data?.verdicts ? verdicts() : {}), ...minResults(), ...field() };
  }
}

function conditionFor(parent: StepData, data: ConditionData | undefined): Record<string, unknown> {
  switch (parent.kind) {
    case "trigger":
      if (parent.trigger_type !== SIGNAL_TRIGGER) return {};
      return data?.min_rating != null ? { min_rating: data.min_rating } : {};
    case "agent":
      return agentConditionFor(parent, data);
    default:
      return {};
  }
}

export function triggerDataOf(nodes: StepNode[]): TriggerData | undefined {
  const data: StepData | undefined = nodes.find((n) => n.id === TRIGGER_ID)?.data;
  return data?.kind === "trigger" ? data : undefined;
}

/**
 * What to save. Walks the tree from the trigger, so a step not connected to
 * it is left out (validation flags those as errors before Save is allowed).
 */
export function toPayload(
  name: string,
  active: boolean,
  nodes: StepNode[],
  edges: ConditionEdge[],
): WorkflowPayload {
  const trigger = triggerDataOf(nodes);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, ConditionEdge[]>();
  for (const e of edges) outgoing.set(e.source, [...(outgoing.get(e.source) ?? []), e]);

  const steps: WorkflowStepInput[] = [];
  const seen = new Set<string>([TRIGGER_ID]);
  const walk = (parentId: string) => {
    const parent = byId.get(parentId);
    if (!parent) return;
    for (const e of outgoing.get(parentId) ?? []) {
      const child = byId.get(e.target);
      if (!child || seen.has(child.id) || child.data.kind === "trigger") continue;
      seen.add(child.id);
      steps.push({
        id: child.id,
        step_type: child.data.kind,
        config: configFor(child.data),
        enabled: child.data.enabled,
        parent_step_id: parentId === TRIGGER_ID ? null : parentId,
        condition: conditionFor(parent.data, e.data),
        requires_approval: !!e.data?.requires_approval,
        position: { x: Math.round(child.position.x), y: Math.round(child.position.y) },
      });
      walk(child.id);
    }
  };
  walk(TRIGGER_ID);

  const trigger_type: TriggerType =
    trigger && isTriggerType(trigger.trigger_type) ? trigger.trigger_type : SIGNAL_TRIGGER;
  // Public data types have no settings and send no `source`.
  const trigger_config: WorkflowTriggerConfig =
    trigger?.trigger_type === SIGNAL_TRIGGER
      ? {
          data_type: trigger.data_type,
          prompt: trigger.prompt,
          ...(trigger.match_mode === "rules" ? { match_mode: "rules" as const } : {}),
          ...(Object.keys(trigger.source).length ? { source: trigger.source } : {}),
        }
      : {};

  return { name: name.trim(), active, trigger_type, trigger_config, steps };
}

/** Stable string for dirty tracking: order-insensitive where the API is. */
export function fingerprint(payload: WorkflowPayload): string {
  const steps = [...payload.steps]
    .sort((a, b) => (a.id ?? "").localeCompare(b.id ?? ""))
    .map((s) => {
      const condition = { ...s.condition };
      if (Array.isArray(condition.verdicts)) {
        condition.verdicts = [...(condition.verdicts as string[])].sort();
      }
      return {
        id: s.id,
        step_type: s.step_type,
        config: sortKeysDeep(s.config),
        enabled: s.enabled,
        parent_step_id: s.parent_step_id,
        condition: sortKeysDeep(condition),
        requires_approval: !!s.requires_approval,
        position: s.position,
      };
    });
  return JSON.stringify({
    name: payload.name,
    active: payload.active,
    trigger_type: payload.trigger_type,
    // Nested too: the order of keys inside `source` must not read as dirty.
    trigger_config: sortKeysDeep(payload.trigger_config),
    steps,
  });
}

// Nested too: the order of keys inside `config.data_sources` must not read as dirty.
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, sortKeysDeep(v)]),
  );
}

export function toCreateBody(payload: WorkflowPayload, tenantId: string): WorkflowCreate {
  return {
    tenant_id: tenantId,
    name: payload.name,
    trigger_type: payload.trigger_type,
    trigger_config: payload.trigger_config,
    active: payload.active,
    steps: payload.steps,
  };
}

export function toUpdateBody(payload: WorkflowPayload): WorkflowUpdate {
  return {
    name: payload.name,
    trigger_config: payload.trigger_config,
    active: payload.active,
    steps: payload.steps,
  };
}
